import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MovieRow from "../components/MovieRow";
import { Play, Plus, Star, ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { resolveUrl } from "../lib/url";

export default function Detail({ kind = "movie" }) {
  const { id } = useParams();
  const { user, refresh } = useAuth();
  const [item, setItem] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [activeSeason, setActiveSeason] = useState(1);

  useEffect(() => {
    api.get(`/${kind === "series" ? "series" : "movies"}/${id}`).then((r) => setItem(r.data));
    api.get(`/${kind === "series" ? "series" : "movies"}`).then((r) => setSimilar(r.data.filter((x) => x.id !== id).slice(0, 12)));
  }, [id, kind]);

  if (!item) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-32 px-8"><div className="skeleton h-[60vh] rounded-2xl" /></div>
      </div>
    );
  }

  const inList = user?.watchlist?.includes(id);

  const toggleList = async () => {
    try {
      if (inList) {
        await api.delete(`/watchlist/${id}`);
        toast.success("Removed from My List");
      } else {
        await api.post(`/watchlist/${id}`);
        toast.success("Added to My List");
      }
      refresh();
    } catch {
      toast.error("Sign in to manage your list");
    }
  };

  const seasonEpisodes = (item.episodes || []).filter((e) => e.seasonNumber === activeSeason);
  const seasonsList = item.totalSeasons ? Array.from({ length: item.totalSeasons }, (_, i) => i + 1) : [];

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="detail-page">
      <Navbar />
      {/* Hero */}
      <div className="relative h-[75vh] min-h-[520px]">
        <img src={resolveUrl(item.bannerUrl || item.posterUrl)} alt={item.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 max-w-[1600px] mx-auto px-4 sm:px-12 flex flex-col justify-end pb-16">
          <div className="max-w-3xl animate-fade-up">
            <h1 className="text-5xl sm:text-7xl font-black tracking-tighter mb-3" style={{ fontFamily: "Outfit", textShadow: "0 4px 24px rgba(0,0,0,0.8)" }}>{item.title}</h1>
            <div className="flex items-center gap-3 text-sm text-[#ddd] mb-4 flex-wrap">
              <span className="flex items-center gap-1"><Star size={14} className="fill-yellow-400 text-yellow-400" />{item.rating?.toFixed(1)}</span>
              <span>{item.releaseYear}</span>
              {item.duration && <span>{item.duration}</span>}
              {item.genre?.length > 0 && <span className="px-2 py-0.5 border border-white/30 rounded text-xs uppercase tracking-wider">{item.genre[0]}</span>}
              {item.totalSeasons && <span>{item.totalSeasons} Season{item.totalSeasons > 1 ? "s" : ""}</span>}
            </div>
            <p className="text-base sm:text-lg text-[#ddd] leading-relaxed mb-6 max-w-2xl">{item.description}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <Link to={`/watch/${kind}/${item.id}${kind === "series" && seasonEpisodes[0] ? `?ep=${seasonEpisodes[0].id}` : ""}`} className="btn-primary" data-testid="detail-play-btn">
                <Play size={18} className="fill-white" /> Play
              </Link>
              <button onClick={toggleList} className="btn-secondary" data-testid="detail-add-list-btn">
                {inList ? <><Check size={18} /> In My List</> : <><Plus size={18} /> Add to My List</>}
              </button>
              <button className="btn-secondary !p-3" data-testid="detail-like-btn"><ThumbsUp size={18} /></button>
              <button className="btn-secondary !p-3" data-testid="detail-dislike-btn"><ThumbsDown size={18} /></button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-12 py-12">
        {/* Cast & Genre */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          <div className="sm:col-span-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#888] mb-2">Synopsis</h3>
            <p className="text-[#ccc] leading-relaxed">{item.description}</p>
          </div>
          <div>
            {item.cast?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#888] mb-2">Cast</h3>
                <p className="text-sm text-[#ccc]">{item.cast.join(", ")}</p>
              </div>
            )}
            {item.genre?.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#888] mb-2">Genres</h3>
                <p className="text-sm text-[#ccc]">{item.genre.join(", ")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Episodes */}
        {kind === "series" && seasonsList.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Episodes</h2>
              <select value={activeSeason} onChange={(e) => setActiveSeason(parseInt(e.target.value))} className="bg-[#1a1a1a] border border-white/10 rounded px-3 py-1.5 text-sm" data-testid="season-selector">
                {seasonsList.map((n) => <option key={n} value={n}>Season {n}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              {seasonEpisodes.map((ep) => (
                <Link key={ep.id} to={`/watch/series/${item.id}?ep=${ep.id}`} className="flex gap-4 p-3 rounded-lg hover:bg-white/5 transition group" data-testid={`episode-${ep.id}`}>
                  <div className="w-40 sm:w-48 flex-shrink-0 aspect-video rounded overflow-hidden bg-[#111] relative">
                    <img src={ep.thumbnailUrl} alt={ep.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <div className="w-10 h-10 bg-white/90 text-black rounded-full flex items-center justify-center"><Play size={16} className="fill-black" /></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl font-black text-[#666]">{ep.episodeNumber}</span>
                      <h4 className="font-semibold">{ep.title}</h4>
                      <span className="ml-auto text-xs text-[#888]">{ep.duration}</span>
                    </div>
                    <p className="text-sm text-[#aaa] mt-1.5 line-clamp-2">{ep.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {similar.length > 0 && (
          <MovieRow title={`More Like ${item.title}`} items={similar} type={kind === "series" ? "series" : "movie"} />
        )}
      </div>
      <Footer />
    </div>
  );
}
